import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  getTicketSyncAnalytics,
  listSyncLogs,
  retryTicketSyncLog,
  retryAllFailedSyncs,
} from '../api/ticketSyncApi'

const PAGE_SIZE = 10
const EMPTY_ANALYTICS = {
  tickets_received: 0,
  tasks_created: 0,
  sync_failures: 0,
  total_syncs: 0,
  sync_success_rate: 0,
  avg_response_time_ms: 0,
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Map a raw sync-log row to a display row. */
function mapLogRow(row) {
  const syncResult = String(row.sync_result || '').toUpperCase()
  const outcome = syncResult || 'UNKNOWN'

  // Details: task_code / job_code on success; error_message on failure
  let details = ''
  if (row.task_code) details = row.task_code
  if (row.job_code) details = details ? `${details} / ${row.job_code}` : row.job_code
  if (!details && row.error_message) details = row.error_message

  return {
    id: Number(row.id),
    syncedAt: formatDate(row.synced_at),
    ticketNo: row.external_ticket_no || row.external_ticket_id || '-',
    eventType: row.event_type || '-',
    clientName: row.client_name || '-',
    assigneeName: row.assignee_name || '-',
    outcome,
    details: details || '-',
    errorMessage: row.error_message || '',
    canRetry: syncResult === 'FAILED',
    raw: row,
  }
}

export function useTicketInflowScreen() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    limit: PAGE_SIZE,
    offset: 0,
    totalCount: 0,
  })
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS)

  const [isLoading, setIsLoading] = useState(true)
  const [retryingId, setRetryingId] = useState(null)
  const [isBulkRetrying, setIsBulkRetrying] = useState(false)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    setExpandedRowId(null)
  }, [page, statusFilter, search])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listSyncLogs({
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: search || undefined,
        })
        if (!active) return
        setRows(response.rows)
        setPagination({
          count: response.count,
          limit: response.limit || PAGE_SIZE,
          offset: response.offset,
          totalCount: response.totalCount || 0,
        })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load sync logs.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }
    loadRows()
    return () => { active = false }
  }, [page, statusFilter, search])

  useEffect(() => {
    let active = true
    async function loadAnalytics() {
      try {
        const data = await getTicketSyncAnalytics()
        if (!active) return
        setAnalytics(data)
      } catch {
        if (!active) return
        setAnalytics(EMPTY_ANALYTICS)
      }
    }
    loadAnalytics()
    return () => { active = false }
  }, [])

  const mappedRows = useMemo(() => rows.map(mapLogRow), [rows])

  const totalPages = Math.max(1, Math.ceil((pagination.totalCount || 0) / PAGE_SIZE))
  const hasMore = page < totalPages
  const rangeStart = mappedRows.length ? (page - 1) * PAGE_SIZE + 1 : 0
  const rangeEnd = mappedRows.length
    ? Math.min(rangeStart + mappedRows.length - 1, pagination.totalCount || 0)
    : 0

  const retryLog = useCallback(
    async (syncId) => {
      setError('')
      setSuccess('')
      setRetryingId(syncId)
      try {
        const result = await retryTicketSyncLog(syncId)
        if (result?.success) {
          setSuccess(result.message ? `Retry succeeded — ${result.message}` : 'Retry succeeded — ticket resolved and moved to Success.')
        } else {
          setError(result?.message || 'Retry failed.')
        }
        const response = await listSyncLogs({
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: search || undefined,
        })
        setRows(response.rows)
        setPagination({
          count: response.count,
          limit: response.limit || PAGE_SIZE,
          offset: response.offset,
          totalCount: response.totalCount || 0,
        })
        try {
          const analyticsData = await getTicketSyncAnalytics()
          setAnalytics(analyticsData)
        } catch {
          // non-blocking analytics refresh
        }
      } catch (retryError) {
        setError(extractApiErrorMessage(retryError, 'Retry request failed.'))
      } finally {
        setRetryingId(null)
      }
    },
    [page, statusFilter, search],
  )

  const handleBulkRetry = useCallback(async () => {
    setError('')
    setSuccess('')
    setIsBulkRetrying(true)
    try {
      const result = await retryAllFailedSyncs()
      if (result?.success) {
        setSuccess(result.message || 'Bulk retry triggered successfully.')
      } else {
        setError(result?.message || 'Bulk retry failed.')
      }
      const response = await listSyncLogs({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: search || undefined,
      })
      setRows(response.rows)
      setPagination({
        count: response.count,
        limit: response.limit || PAGE_SIZE,
        offset: response.offset,
        totalCount: response.totalCount || 0,
      })
      try {
        const analyticsData = await getTicketSyncAnalytics()
        setAnalytics(analyticsData)
      } catch {
        // non-blocking analytics refresh
      }
    } catch (bulkError) {
      setError(extractApiErrorMessage(bulkError, 'Bulk retry request failed.'))
    } finally {
      setIsBulkRetrying(false)
    }
  }, [page, statusFilter, search])

  function resetPageAndSet(setter, value) {
    setter(value)
    setPage(1)
  }

  return {
    state: {
      isLoading,
      retryingId,
      isBulkRetrying,
      expandedRowId,
      error,
      success,
      rows: mappedRows,
      page,
      hasMore,
      rangeStart,
      rangeEnd,
      statusFilter,
      search,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalPages,
      analytics,
      totalCount: pagination.totalCount || 0,
    },
    actions: {
      setStatusFilter: (value) => resetPageAndSet(setStatusFilter, value),
      setSearch: (value) => resetPageAndSet(setSearch, value),
      goToPage: (next) => {
        if (next < 1) return
        if (next > totalPages) return
        setPage(next)
      },
      goToPreviousPage: () => setPage((prev) => Math.max(1, prev - 1)),
      goToNextPage: () => { if (hasMore) setPage((prev) => prev + 1) },
      retryLog,
      handleBulkRetry,
      toggleExpandedRow: (rowId) => setExpandedRowId((prev) => (prev === rowId ? null : rowId)),
    },
  }
}
